/**
 * Budget Service
 *
 * Handles all forecast budget (budget prévisionnel) API calls.
 */

import api from './api';
import type {
  ApiResponse,
  Budget,
  BudgetSummary,
  BudgetVersion,
  BudgetLimitResponse,
  BudgetFilters,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  DuplicateBudgetRequest,
  CreateBudgetFromStatementRequest,
  PaginatedResponse,
} from '@budgira/shared';

export const budgetService = {
  /**
   * Get all budgets with filters and pagination
   */
  async list(
    filters: BudgetFilters = {},
    page: number = 1,
    pageSize: number = 10
  ): Promise<PaginatedResponse<BudgetSummary>> {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());

    if (filters.year) {
      params.set('year', filters.year.toString());
    }
    if (filters.status) {
      params.set('status', filters.status);
    }
    if (filters.accountId) {
      params.set('accountId', filters.accountId);
    }
    if (filters.search) {
      params.set('search', filters.search);
    }

    const response = await api.get<ApiResponse<PaginatedResponse<BudgetSummary>>>(
      `/budgets?${params.toString()}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch budgets');
    }
    return response.data.data;
  },

  /**
   * Get a specific budget by ID
   */
  async getById(id: string): Promise<Budget> {
    const response = await api.get<ApiResponse<Budget>>(
      `/budgets/${id}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch budget');
    }
    return response.data.data;
  },

  /**
   * Create a new budget
   */
  async create(data: CreateBudgetRequest): Promise<Budget> {
    const response = await api.post<ApiResponse<Budget>>(
      '/budgets',
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create budget');
    }
    return response.data.data;
  },

  /**
   * Create a budget from an accounting statement
   */
  async createFromStatement(data: CreateBudgetFromStatementRequest): Promise<Budget> {
    const response = await api.post<ApiResponse<Budget>>(
      '/budgets/from-statement',
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create budget from statement');
    }
    return response.data.data;
  },

  /**
   * Update an existing budget
   */
  async update(
    id: string,
    data: UpdateBudgetRequest
  ): Promise<Budget> {
    const response = await api.put<ApiResponse<Budget>>(
      `/budgets/${id}`,
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to update budget');
    }
    return response.data.data;
  },

  /**
   * Delete a budget
   */
  async delete(id: string): Promise<void> {
    const response = await api.delete<ApiResponse<null>>(`/budgets/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete budget');
    }
  },

  /**
   * Duplicate an existing budget
   */
  async duplicate(id: string, data: DuplicateBudgetRequest): Promise<Budget> {
    const response = await api.post<ApiResponse<Budget>>(
      `/budgets/${id}/duplicate`,
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to duplicate budget');
    }
    return response.data.data;
  },

  /**
   * Get version history for a budget
   */
  async getVersionHistory(budgetId: string): Promise<BudgetVersion[]> {
    const response = await api.get<ApiResponse<BudgetVersion[]>>(
      `/budgets/${budgetId}/versions`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch version history');
    }
    return response.data.data;
  },

  /**
   * Get a specific version
   */
  async getVersion(budgetId: string, version: number): Promise<BudgetVersion> {
    const response = await api.get<ApiResponse<BudgetVersion>>(
      `/budgets/${budgetId}/versions/${version}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch version');
    }
    return response.data.data;
  },

  /**
   * Restore a budget to a specific version
   */
  async restoreVersion(budgetId: string, version: number): Promise<Budget> {
    const response = await api.post<ApiResponse<Budget>>(
      `/budgets/${budgetId}/versions/${version}/restore`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to restore version');
    }
    return response.data.data;
  },

  /**
   * Check user's budget limit
   */
  async checkLimit(): Promise<BudgetLimitResponse> {
    const response = await api.get<ApiResponse<BudgetLimitResponse>>(
      '/budgets/limit'
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to check limit');
    }
    return response.data.data;
  },

  /**
   * Export a budget as PDF (returns blob)
   */
  async exportPdf(id: string): Promise<Blob> {
    const response = await api.get(`/budgets/${id}/export/pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};

export default budgetService;
